import clsx from "clsx";
import { useMemo } from "react";
import { AgingReport } from "common";
import ReportMessages from "./ReportMessages";
import { Avatar } from "./ReportMessage";
import { getThreadStatus } from "../util/alerts";
import "./ChatDock.scss";

export interface DockedChat {
  /** The report's sort key, which identifies the conversation */
  sk: string;
  minimized: boolean;
}

/** How many chat windows are open at once; opening another closes the oldest */
export const MAX_DOCKED_CHATS = 3;

const Window = ({
  report,
  minimized,
  username,
  onToggle,
  onClose,
  onExpand,
}: {
  report: AgingReport;
  minimized: boolean;
  username?: string;
  onToggle: () => void;
  onClose: () => void;
  onExpand: () => void;
}) => {
  const counts = useMemo(() => {
    const statuses = (report.messages ?? []).filter((message) => !message.archivedAt).map((message) => getThreadStatus(report, message, username));
    return { overdue: statuses.filter((status) => status.overdue).length, fresh: statuses.filter((status) => status.isNew).length };
  }, [report, username]);

  const stop = (action: () => void) => (event: React.MouseEvent) => {
    event.stopPropagation();
    action();
  };

  return (
    <section className={clsx("dock-window", { "is-minimized": minimized })} aria-label={`Chat: ${report.residentName}`}>
      <header className="dock-head" onClick={onToggle}>
        <Avatar name={report.residentName} />
        <button type="button" className="dock-title" aria-expanded={!minimized} aria-label={`${minimized ? "Restore" : "Minimize"} chat with ${report.residentName}`} onClick={stop(onToggle)}>
          <strong>{report.residentName}</strong>
          <small>{report.billType}</small>
        </button>
        {counts.overdue > 0 && (
          <span className="dock-badge is-overdue" title={`${counts.overdue} overdue`}>
            <i className="fa-solid fa-clock" aria-hidden="true" />
            {counts.overdue}
          </span>
        )}
        {counts.fresh > 0 && (
          <span className="dock-badge is-new" title={`${counts.fresh} new`}>
            <i className="fa-solid fa-envelope" aria-hidden="true" />
            {counts.fresh}
          </span>
        )}
        <span className="dock-actions">
          <button type="button" aria-label="Open in full view" title="Open in full view" onClick={stop(onExpand)}>
            <i className="fa-solid fa-up-right-and-down-left-from-center" aria-hidden="true" />
          </button>
          <button type="button" aria-label="Close chat" title="Close chat" onClick={stop(onClose)}>
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </span>
      </header>
      {/* Stays mounted while minimized, so a half-written message is not lost */}
      <div className="dock-body" hidden={minimized}>
        <ReportMessages report={report} onCancel={onClose} docked />
      </div>
    </section>
  );
};

/** Messenger-style chat windows along the bottom of the page: one per resident, minimizable, kept while you browse the table */
export default function ChatDock({
  chats,
  reports,
  username,
  onToggle,
  onClose,
  onExpand,
}: {
  chats: DockedChat[];
  reports: AgingReport[];
  username?: string;
  onToggle: (sk: string) => void;
  onClose: (sk: string) => void;
  onExpand: (report: AgingReport) => void;
}) {
  const open = chats
    .map((chat) => ({ chat, report: reports.find((candidate) => candidate.sk === chat.sk) }))
    .filter((entry): entry is { chat: DockedChat; report: AgingReport } => Boolean(entry.report));
  if (!open.length) return null;

  return (
    <aside className="chat-dock" aria-label="Chat windows">
      {open.map(({ chat, report }) => (
        <Window
          key={chat.sk}
          report={report}
          minimized={chat.minimized}
          username={username}
          onToggle={() => onToggle(chat.sk)}
          onClose={() => onClose(chat.sk)}
          onExpand={() => onExpand(report)}
        />
      ))}
    </aside>
  );
}
